import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { CustomDataTable } from "./CustomDataTable";
import { TableActionBar } from "./TableActionBar";
import { TableColumnManager } from "./TableColumnManager";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { useTableSettings } from "@/hooks/useTableSettings";
import { cn } from "@/lib/utils";

export interface SearchableColumn {
  id: string;
  header: string;
  accessorKey?: string;
  cell?: (row: any) => React.ReactNode;
  sortable?: boolean;
  minWidth?: number;
  width?: number;
  hide?: boolean;
  editable?: boolean;
  searchable?: boolean; // Whether this column should be included in search
}

interface SearchableDataTableProps {
  // Data props
  data: any[];
  columns: SearchableColumn[];
  loading?: boolean;
  hasError?: boolean;
  
  // Search configuration
  searchPlaceholder?: string;
  searchableFields?: string[]; // Fields to search in, defaults to all string fields
  enableInstantSearch?: boolean; // Client-side vs server-side search
  
  // Table functionality
  enableRowSelection?: boolean;
  selectedRows?: string[];
  onRowSelectionChange?: (rowIds: string[]) => void;
  enableColumnResizing?: boolean;
  enableSorting?: boolean;
  
  // Action bar
  actionBarChildren?: React.ReactNode;
  onDeleteSelected?: () => void;
  
  // Pagination
  totalItems?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  showPagination?: boolean;
  
  // Table settings
  tableId: string; // Unique identifier for table settings persistence
  
  // Search handlers for server-side search
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  onSearch?: () => void;
  
  // Empty states
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateAction?: React.ReactNode;
  noResultsTitle?: string;
  noResultsDescription?: string;
  
  // Loading overlay
  isProcessing?: boolean;
  processingProgress?: { current: number; total: number; url?: string } | null;
  
  // Style
  className?: string;
  actionBarClassName?: string;
  
  // Database sorting props
  dbSortColumn?: string | null;
  dbSortDirection?: 'asc' | 'desc' | null;
  onDbSort?: (column: string | null, direction: 'asc' | 'desc' | null) => void;
  disableClientSorting?: boolean;
}

export function SearchableDataTable({
  // Data props
  data,
  columns,
  loading = false,
  hasError = false,
  
  // Search configuration
  searchPlaceholder = "Search...",
  searchableFields,
  enableInstantSearch = false, // Changed default to false for server-side search
  
  // Table functionality
  enableRowSelection = false,
  selectedRows = [],
  onRowSelectionChange,
  enableColumnResizing = false,
  enableSorting = false,
  
  // Action bar
  actionBarChildren,
  onDeleteSelected,
  
  // Pagination
  totalItems,
  pageSize = 50,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  showPagination = true,
  
  // Table settings
  tableId,
  
  // Search handlers for server-side search
  searchTerm = "",
  onSearchChange,
  onSearch,
  
  // Empty states
  emptyStateTitle = "No data found",
  emptyStateDescription = "There is no data to display",
  emptyStateAction,
  noResultsTitle = "No matching results found",
  noResultsDescription,
  
  // Loading overlay
  isProcessing = false,
  processingProgress = null,
  
  // Style
  className,
  actionBarClassName,
  
  // Database sorting props
  dbSortColumn,
  dbSortDirection,
  onDbSort,
  disableClientSorting = false
}: SearchableDataTableProps) {
  // Internal search state for client-side search only
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [isSearchTransition, setIsSearchTransition] = useState(false);
  
  // Use external search term for server-side search, internal for client-side
  const activeSearchTerm = enableInstantSearch ? internalSearchTerm : searchTerm;
  
  // Table settings
  const columnDefs = columns.map(col => ({ id: col.id, header: col.header }));
  const tableSettings = useTableSettings(tableId, columnDefs.map(col => col.id));
  
  // Determine searchable fields for client-side search only
  const defaultSearchableFields = useMemo(() => {
    if (!enableInstantSearch) return []; // No client-side filtering for server-side search
    if (searchableFields) return searchableFields;
    
    // Auto-detect searchable fields from columns
    return columns
      .filter(col => col.searchable !== false && col.accessorKey)
      .map(col => col.accessorKey!)
      .filter(Boolean);
  }, [columns, searchableFields, enableInstantSearch]);
  
  // Client-side search filtering (only when enableInstantSearch is true)
  const filteredData = useMemo(() => {
    if (!enableInstantSearch || !activeSearchTerm.trim()) {
      return data;
    }
    
    const searchLower = activeSearchTerm.toLowerCase();
    
    return data.filter(row => {
      return defaultSearchableFields.some(field => {
        const value = row[field];
        if (value == null) return false;
        
        // Handle arrays (like pic, campaign_bu)
        if (Array.isArray(value)) {
          return value.some(item => 
            String(item).toLowerCase().includes(searchLower)
          );
        }
        
        // Handle regular values
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, activeSearchTerm, defaultSearchableFields, enableInstantSearch]);
  
  // Handle search with proper server-side or client-side logic
  const handleSearchChange = useCallback((value: string) => {
    if (enableInstantSearch) {
      // Client-side search
      setInternalSearchTerm(value);
      setIsSearchTransition(true);
      const timer = setTimeout(() => {
        setIsSearchTransition(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Server-side search
      if (onSearchChange) {
        onSearchChange(value);
      }
    }
  }, [enableInstantSearch, onSearchChange]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !enableInstantSearch && onSearch) {
      onSearch();
    }
  }, [enableInstantSearch, onSearch]);
  
  // State determination logic
  const isSearching = activeSearchTerm.trim().length > 0;
  const hasOriginalData = Array.isArray(data) && data.length > 0;
  const hasFilteredData = enableInstantSearch ? filteredData.length > 0 : hasOriginalData;
  const isReallyLoading = loading || (enableInstantSearch && isSearchTransition);
  
  // Determine what to show
  const showEmptyState = !hasOriginalData && !isReallyLoading && !isSearching;
  const showNoSearchResults = isSearching && !hasFilteredData && !isReallyLoading;
  const showTable = hasOriginalData && !hasError;
  
  // For server-side search, use data as-is (already filtered and paginated by server)
  // For client-side search, use filtered data
  const displayData = enableInstantSearch ? filteredData : data;
  
  // Calculate pagination totals
  const paginationTotalItems = useMemo(() => {
    if (enableInstantSearch && isSearching) {
      // Client-side search: use filtered results count
      return filteredData.length;
    } else {
      // Server-side search or no search: use provided totalItems
      return totalItems || data.length;
    }
  }, [enableInstantSearch, isSearching, filteredData.length, totalItems, data.length]);

  // Calculate paginated display data (only needed for client-side search)
  const paginatedDisplayData = useMemo(() => {
    if (!enableInstantSearch) {
      // Server-side: data is already paginated by server
      return displayData;
    }
    
    if (!isSearching) {
      // Client-side but no search: use data as-is
      return displayData;
    }
    
    // Client-side search: paginate the filtered results
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return displayData.slice(startIndex, endIndex);
  }, [displayData, enableInstantSearch, isSearching, currentPage, pageSize]);
  
  console.log(`SearchableDataTable: enableInstantSearch=${enableInstantSearch}, isSearching=${isSearching}, data.length=${data.length}, filteredData.length=${enableInstantSearch ? filteredData.length : 'N/A'}, totalItems=${totalItems}, paginationTotalItems=${paginationTotalItems}, paginatedDisplayData.length=${paginatedDisplayData.length}`);
  
  return (
    <div className={cn("space-y-4", className)}>
      {/* Action Bar with integrated search */}
      <div className={cn(
        "flex justify-between items-center gap-3 p-3 bg-card/50 rounded-lg border border-border/50",
        actionBarClassName
      )}>
        <div className="flex items-center gap-3">
          {actionBarChildren}
          
          <TableActionBar 
            selectedRowsCount={selectedRows.length} 
            onDeleteSelected={onDeleteSelected}
          />
          
          {isProcessing && (
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span>Processing...</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Integrated Search Bar */}
          <div className="relative w-[250px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              className="pl-8 bg-background border-input text-foreground"
              value={activeSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          
          <TableColumnManager
            columns={columnDefs}
            columnVisibility={tableSettings.columnVisibility}
            onColumnVisibilityChange={tableSettings.updateColumnVisibility}
            onResetColumns={tableSettings.resetSettings}
          />
        </div>
      </div>

      {/* Content Area */}
      {hasError ? (
        <div className="p-8 text-center border border-dashed rounded-md bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <h3 className="text-lg font-medium text-red-900 dark:text-red-100">
            Failed to load data
          </h3>
          <p className="text-red-700 dark:text-red-300 mt-2">
            There was an error loading the data. Please check your connection and try again.
          </p>
        </div>
      ) : showEmptyState ? (
        <div className="p-8 text-center border border-dashed rounded-md bg-muted/20">
          <h3 className="text-lg font-medium">{emptyStateTitle}</h3>
          <p className="text-muted-foreground mt-2">{emptyStateDescription}</p>
          {emptyStateAction && (
            <div className="mt-4">
              {emptyStateAction}
            </div>
          )}
        </div>
      ) : showNoSearchResults ? (
        <div className="p-8 text-center border border-dashed rounded-md bg-muted/20">
          <h3 className="text-lg font-medium">{noResultsTitle}</h3>
          <p className="text-muted-foreground mt-2">
            {noResultsDescription || `No results found for "${activeSearchTerm}". Try adjusting your search term.`}
          </p>
        </div>
      ) : showTable ? (
        <div className="overflow-x-auto relative">
          <TableLoadingOverlay 
            isLoading={isProcessing || (enableInstantSearch && isSearchTransition)} 
            showProgress={!!processingProgress}
            progress={processingProgress}
          />
          <div className="w-full relative">
            <CustomDataTable
              data={paginatedDisplayData}
              columns={columns}
              loading={isReallyLoading}
              enableRowSelection={enableRowSelection}
              selectedRows={selectedRows}
              onRowSelectionChange={onRowSelectionChange}
              enableColumnResizing={enableColumnResizing}
              onColumnWidthChange={tableSettings.updateColumnWidth}
              currentColumnWidths={tableSettings.columnWidths}
              defaultColumnVisibility={tableSettings.columnVisibility}
              onColumnVisibilityChange={tableSettings.updateColumnVisibility}
              enableSorting={enableSorting}
              // Pass through database sorting props
              dbSortColumn={dbSortColumn}
              dbSortDirection={dbSortDirection}
              onDbSort={onDbSort}
              disableClientSorting={disableClientSorting}
              totalItems={paginationTotalItems}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              showPagination={showPagination}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
